import { useState, useEffect } from 'react';
import {
  MessageSquare,
  Search,
  Eye,
  Trash2,
  Pin,
  Lock,
  Unlock,
  RefreshCw,
  Download,
  User,
  Calendar,
  Hash,
  AlertTriangle,
  XCircle,
  CheckCircle
} from 'lucide-react';
import { UserRole } from '../types';
import { getMedicalRoleLabel } from '../utils/medicalRoles';
import { getRoleDisplay as getUtilRoleDisplay, getRoleBadgeClass as getUtilRoleBadgeClass } from '../utils/roles';

interface ForumPost {
  id: string;
  title: string;
  content: string;
  is_pinned: boolean;
  is_locked: boolean;
  view_count: number;
  reply_count: number;
  created_at: string;
  updated_at: string;
  first_name: string;
  last_name: string;
  email: string;
  role: UserRole | string | null;
  medical_role?: string;
  category_name: string;
  category_color: string;
}

interface ForumCategory {
  id: string;
  name: string;
  description?: string;
  color: string;
  is_active: boolean;
  post_count: number;
}

interface ForumFilters {
  search: string;
  category_id: string;
  page: number;
  limit: number;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  type: 'danger' | 'warning' | 'info';
  confirmText?: string;
  cancelText?: string;
}

function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type,
  confirmText = 'Επιβεβαίωση',
  cancelText = 'Ακύρωση'
}: ConfirmationModalProps) {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: <AlertTriangle className="w-6 h-6 text-red-400" />,
          button: 'bg-red-600 hover:bg-red-700 text-white'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: <AlertTriangle className="w-6 h-6 text-yellow-400" />,
          button: 'bg-yellow-600 hover:bg-yellow-700 text-white'
        };
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: <CheckCircle className="w-6 h-6 text-blue-400" />,
          button: 'bg-blue-600 hover:bg-blue-700 text-white'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className={`flex items-center p-6 ${styles.bg} ${styles.border} border-b rounded-t-lg`}>
          {styles.icon}
          <h3 className="ml-3 text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="p-6">
          <p className="text-gray-700">{message}</p>
        </div>
        <div className="flex justify-end space-x-3 p-6 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-md transition-colors ${styles.button}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ForumManagement() {
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<ForumPost | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState<any>({});
  
  const [filters, setFilters] = useState<ForumFilters>({
    search: '',
    category_id: '',
    page: 1,
    limit: 20
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
    hasNext: false,
    hasPrevious: false
  });

  useEffect(() => {
    fetchForumData();
  }, [filters]);

  const fetchForumData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.set('search', filters.search);
      if (filters.category_id) queryParams.set('category_id', filters.category_id);
      queryParams.set('page', filters.page.toString());
      queryParams.set('limit', filters.limit.toString());

      const response = await fetch(`/api/v1/admin/forum?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Αποτυχία λήψης δεδομένων φόρουμ');
      }

      const result = await response.json();
      setPosts(result.data.posts);
      setCategories(result.data.categories);
      setPagination(result.data.pagination);
    } catch (error) {
      console.error('Fetch forum data error:', error);
      setError(error instanceof Error ? error.message : 'Άγνωστο σφάλμα');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (reason: string = '') => {
    if (!selectedPost) return;

    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`/api/v1/admin/forum/posts/${selectedPost.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        throw new Error('Αποτυχία διαγραφής δημοσίευσης');
      }

      // Remove from local state
      setPosts(prev => prev.filter(post => post.id !== selectedPost.id));

      setShowConfirmModal(false);
      setSelectedPost(null);
    } catch (error) {
      console.error('Delete post error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα διαγραφής δημοσίευσης');
    }
  };

  const handlePinPost = async () => {
    if (!selectedPost) return;

    try {
      const token = localStorage.getItem('accessToken');
      const newPinnedState = !selectedPost.is_pinned;

      const response = await fetch(`/api/v1/admin/forum/posts/${selectedPost.id}/moderate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          action: 'pin',
          value: newPinnedState,
          reason: `${newPinnedState ? 'Καρφίτσωμα' : 'Αφαίρεση καρφίτσας'} από διαχειριστή στις ${new Date().toLocaleDateString('el-GR')}`
        })
      });

      if (!response.ok) {
        throw new Error(`Αποτυχία ${newPinnedState ? 'καρφιτσώματος' : 'αφαίρεσης καρφίτσας'} δημοσίευσης`);
      }

      // Update local state
      setPosts(prev => prev.map(post => 
        post.id === selectedPost.id 
          ? { ...post, is_pinned: newPinnedState }
          : post
      ));

      setShowConfirmModal(false);
      setSelectedPost(null);
    } catch (error) {
      console.error('Pin post error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα καρφιτσώματος δημοσίευσης');
    }
  };

  const handleLockPost = async () => {
    if (!selectedPost) return;

    try {
      const token = localStorage.getItem('accessToken');
      const newLockedState = !selectedPost.is_locked;

      const response = await fetch(`/api/v1/admin/forum/posts/${selectedPost.id}/moderate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          action: 'lock',
          value: newLockedState,
          reason: `${newLockedState ? 'Κλείδωμα' : 'Ξεκλείδωμα'} από διαχειριστή στις ${new Date().toLocaleDateString('el-GR')}`
        })
      });

      if (!response.ok) {
        throw new Error(`Αποτυχία ${newLockedState ? 'κλειδώματος' : 'ξεκλειδώματος'} δημοσίευσης`);
      }

      // Update local state
      setPosts(prev => prev.map(post => 
        post.id === selectedPost.id 
          ? { ...post, is_locked: newLockedState }
          : post
      ));

      setShowConfirmModal(false);
      setSelectedPost(null);
    } catch (error) {
      console.error('Lock post error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα κλειδώματος δημοσίευσης');
    }
  };

  // Closure-based handler functions that accept the post directly
  const handleDeletePostById = async (post: ForumPost, reason: string = '') => {
    console.log('[DEBUG] ForumManagement handleDeletePostById called with post:', post.id);
    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`/api/v1/admin/forum/posts/${post.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        throw new Error('Αποτυχία διαγραφής δημοσίευσης');
      }

      // Remove from local state
      setPosts(prev => prev.filter(p => p.id !== post.id));

      setShowConfirmModal(false);
      setSelectedPost(null);
    } catch (error) {
      console.error('Delete post error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα διαγραφής δημοσίευσης');
    }
  };

  const handlePinPostById = async (post: ForumPost) => {
    console.log('[DEBUG] ForumManagement handlePinPostById called with post:', post.id);
    try {
      const token = localStorage.getItem('accessToken');
      const newPinnedState = !post.is_pinned;

      const response = await fetch(`/api/v1/admin/forum/posts/${post.id}/moderate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          action: 'pin',
          value: newPinnedState,
          reason: `${newPinnedState ? 'Καρφίτσωμα' : 'Αφαίρεση καρφίτσας'} από διαχειριστή στις ${new Date().toLocaleDateString('el-GR')}`
        })
      });

      if (!response.ok) {
        throw new Error(`Αποτυχία ${newPinnedState ? 'καρφιτσώματος' : 'αφαίρεσης καρφίτσας'} δημοσίευσης`);
      }

      // Update local state
      setPosts(prev => prev.map(p => 
        p.id === post.id 
          ? { ...p, is_pinned: newPinnedState }
          : p
      ));

      setShowConfirmModal(false);
      setSelectedPost(null);
    } catch (error) {
      console.error('Pin post error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα καρφιτσώματος δημοσίευσης');
    }
  };

  const handleLockPostById = async (post: ForumPost) => {
    console.log('[DEBUG] ForumManagement handleLockPostById called with post:', post.id);
    try {
      const token = localStorage.getItem('accessToken');
      const newLockedState = !post.is_locked;

      const response = await fetch(`/api/v1/admin/forum/posts/${post.id}/moderate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          action: 'lock',
          value: newLockedState,
          reason: `${newLockedState ? 'Κλείδωμα' : 'Ξεκλείδωμα'} από διαχειριστή στις ${new Date().toLocaleDateString('el-GR')}`
        })
      });

      if (!response.ok) {
        throw new Error(`Αποτυχία ${newLockedState ? 'κλειδώματος' : 'ξεκλειδώματος'} δημοσίευσης`);
      }

      // Update local state
      setPosts(prev => prev.map(p => 
        p.id === post.id 
          ? { ...p, is_locked: newLockedState }
          : p
      ));

      setShowConfirmModal(false);
      setSelectedPost(null);
    } catch (error) {
      console.error('Lock post error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα κλειδώματος δημοσίευσης');
    }
  };

  const openConfirmModal = (post: ForumPost, action: string) => {
    setSelectedPost(post);

    // Create handler functions that capture the current post and action
    const handleConfirmAction = () => {
      console.log('[DEBUG] ForumManagement handleConfirmAction called with action:', action, 'post:', post.id);
      switch (action) {
        case 'delete':
          console.log('[DEBUG] Calling handleDeletePostById()');
          handleDeletePostById(post);
          break;
        case 'pin':
          console.log('[DEBUG] Calling handlePinPostById()');
          handlePinPostById(post);
          break;
        case 'lock':
          console.log('[DEBUG] Calling handleLockPostById()');
          handleLockPostById(post);
          break;
        default:
          console.log('[DEBUG] Unknown action:', action);
      }
    };

    let config = {};
    switch (action) {
      case 'delete':
        config = {
          title: 'Διαγραφή Δημοσίευσης',
          message: `Είστε σίγουροι ότι θέλετε να διαγράψετε τη δημοσίευση "${post.title}"; Αυτή η ενέργεια θα διαγράψει επίσης όλες τις απαντήσεις.`,
          type: 'danger',
          confirmText: 'Διαγραφή',
          onConfirm: handleConfirmAction
        };
        break;
      case 'pin':
        config = {
          title: post.is_pinned ? 'Αφαίρεση Καρφίτσας' : 'Καρφίτσωμα Δημοσίευσης',
          message: `Είστε σίγουροι ότι θέλετε να ${post.is_pinned ? 'αφαιρέσετε την καρφίτσα από' : 'καρφιτσώσετε'} τη δημοσίευση "${post.title}";`,
          type: 'info',
          confirmText: post.is_pinned ? 'Αφαίρεση Καρφίτσας' : 'Καρφίτσωμα',
          onConfirm: handleConfirmAction
        };
        break;
      case 'lock':
        config = {
          title: post.is_locked ? 'Ξεκλείδωμα Δημοσίευσης' : 'Κλείδωμα Δημοσίευσης',
          message: `Είστε σίγουροι ότι θέλετε να ${post.is_locked ? 'ξεκλειδώσετε' : 'κλειδώσετε'} τη δημοσίευση "${post.title}";`,
          type: post.is_locked ? 'info' : 'warning',
          confirmText: post.is_locked ? 'Ξεκλείδωμα' : 'Κλείδωμα',
          onConfirm: handleConfirmAction
        };
        break;
    }

    setConfirmConfig(config);
    setShowConfirmModal(true);
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const getRoleDisplay = (role: UserRole | null | undefined | string): string => {
    if (!role) {
      return '👤 Χρήστης'; // Default fallback for null/undefined roles
    }

    // Handle legacy 'user' or 'doctor' role by treating it as 'member'
    if (role === 'user' || role === 'doctor') {
      return getUtilRoleDisplay('member');
    }

    // Handle legacy 'consultant_emeritus' as 'honorary_member'
    if (role === 'consultant_emeritus') {
      return getUtilRoleDisplay('honorary_member');
    }

    // If it's a valid UserRole, use the utility function
    if (['member', 'honorary_member', 'moderator', 'admin'].includes(role as string)) {
      return getUtilRoleDisplay(role as UserRole);
    }

    // Fallback for unknown roles
    return `👤 ${role}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <XCircle className="w-5 h-5 text-red-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Σφάλμα φόρτωσης</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={fetchForumData}
          className="mt-4 bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
        >
          Δοκιμή ξανά
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Διαχείριση Φόρουμ</h1>
            <p className="text-gray-600 mt-1">
              Συντονισμός {pagination.total.toLocaleString()} δημοσιεύσεων σε {categories.length} κατηγορίες
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={fetchForumData}
              className="flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Ανανέωση
            </button>
            <button className="flex items-center px-3 py-2 bg-medical-600 hover:bg-medical-700 text-white rounded-md transition-colors">
              <Download className="w-4 h-4 mr-2" />
              Εξαγωγή
            </button>
          </div>
        </div>
      </div>

      {/* Categories Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Κατηγορίες Φόρουμ</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => (
            <div key={category.id} className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: category.color }}
                  />
                  <h4 className="font-medium text-gray-900">{category.name}</h4>
                </div>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  category.is_active 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {category.is_active ? 'Ενεργή' : 'Ανενεργή'}
                </span>
              </div>
              {category.description && (
                <p className="text-sm text-gray-600 mb-2">{category.description}</p>
              )}
              <div className="flex items-center text-xs text-gray-500">
                <Hash className="w-3 h-3 mr-1" />
                {category.post_count} δημοσιεύσεις
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Αναζήτηση
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                placeholder="Τίτλος, περιεχόμενο..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Κατηγορία
            </label>
            <select
              value={filters.category_id}
              onChange={(e) => setFilters(prev => ({ ...prev, category_id: e.target.value, page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Όλες οι κατηγορίες</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ανά σελίδα
            </label>
            <select
              value={filters.limit}
              onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ search: '', category_id: '', page: 1, limit: 20 })}
              className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
            >
              Καθαρισμός Φίλτρων
            </button>
          </div>
        </div>
      </div>

      {/* Posts Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Δημοσίευση
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Συγγραφέας
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Κατηγορία
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Δραστηριότητα
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Κατάσταση
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ημερομηνία
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ενέργειες
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {posts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      <div className="flex items-center space-x-2">
                        {post.is_pinned && (
                          <Pin className="w-4 h-4 text-blue-600" title="Καρφιτσωμένη" />
                        )}
                        {post.is_locked && (
                          <Lock className="w-4 h-4 text-red-600" title="Κλειδωμένη" />
                        )}
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {post.title}
                        </div>
                      </div>
                      <div className="text-sm text-gray-500 truncate mt-1">
                        {post.content.substring(0, 100)}...
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {post.first_name} {post.last_name}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <span className="text-xs text-gray-500">
                            {getRoleDisplay(post.role)}
                          </span>
                          {post.medical_role && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {getMedicalRoleLabel(post.medical_role, post.sex)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: post.category_color }}
                      />
                      <span className="text-sm text-gray-900">{post.category_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        {post.view_count}
                      </div>
                      <div className="flex items-center">
                        <MessageSquare className="w-4 h-4 mr-1" />
                        {post.reply_count}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {post.is_pinned && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Καρφιτσωμένη
                        </span>
                      )}
                      {post.is_locked && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Κλειδωμένη
                        </span>
                      )}
                      {!post.is_pinned && !post.is_locked && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Ενεργή
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(post.created_at).toLocaleDateString('el-GR')}
                      </div>
                      {post.updated_at !== post.created_at && (
                        <div className="text-xs text-gray-400 mt-1">
                          Ενημ: {new Date(post.updated_at).toLocaleDateString('el-GR')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => window.open(`/forum/post/${post.id}`, '_blank')}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        title="Προβολή δημοσίευσης"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => openConfirmModal(post, 'pin')}
                        className={`p-1 rounded ${
                          post.is_pinned 
                            ? 'text-blue-600 hover:text-blue-900' 
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                        title={post.is_pinned ? 'Αφαίρεση καρφίτσας' : 'Καρφίτσωμα'}
                      >
                        <Pin className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => openConfirmModal(post, 'lock')}
                        className={`p-1 rounded ${
                          post.is_locked 
                            ? 'text-red-600 hover:text-red-900' 
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                        title={post.is_locked ? 'Ξεκλείδωμα' : 'Κλείδωμα'}
                      >
                        {post.is_locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                      </button>
                      
                      <button
                        onClick={() => openConfirmModal(post, 'delete')}
                        className="text-red-600 hover:text-red-900 p-1 rounded"
                        title="Διαγραφή δημοσίευσης"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrevious}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Προηγούμενη
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNext}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Επόμενη
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Εμφάνιση{' '}
                  <span className="font-medium">
                    {((pagination.page - 1) * pagination.limit) + 1}
                  </span>{' '}
                  έως{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  από{' '}
                  <span className="font-medium">{pagination.total}</span> αποτελέσματα
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrevious}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Προηγούμενη
                  </button>
                  
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const page = Math.max(1, Math.min(
                      pagination.page - 2 + i,
                      pagination.pages - 4 + i
                    ));
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pagination.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNext}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Επόμενη
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setSelectedPost(null);
          setConfirmConfig({});
        }}
        {...confirmConfig}
      />
    </div>
  );
}